import * as React from "react";
import { observer } from "mobx-react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { SortDirection, SortDirectionType } from "react-virtualized";
import {
  ActiveFilters,
  FilterConfig,
  ScrollableTable,
  TableColumnProps,
} from "Components/ScrollableTable/ScrollableTable";
import { bcfViewerStore } from "./BCFViewerStore";
import { BCFViewerDrawer } from "./BCFViewerDrawer";
import { Input } from "antd";

export interface BCFViewerModalProps extends ModalComponentProps, WithTranslation {
  fileId: number;
}
type BcfAllFilters = {
  [key in keyof API_BcfExtensions]: FilterConfig[];
};
type BcfActiveFilters = {
  [key in keyof API_BcfTopic]: string[];
};
export interface BCFViewerModalState {
  visible: boolean;
  highlightedRowId: string[];
  sortBy: keyof API_BcfTopic;
  searchBy: string;
  filterByStatus: string[];
  filterByPriority: string[];
  topics: API_BcfTopic[];
  sortDirection: SortDirectionType;
  availableFilters: BcfAllFilters;
  activeFilters: BcfActiveFilters;
  allFilters: ActiveFilters;
}

export const BCFViewerModal = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<BCFViewerModalProps, BCFViewerModalState> {
      private table: React.RefObject<ScrollableTable>;

      constructor(props: BCFViewerModalProps) {
        super(props);
        this.state = {
          visible: false,
          highlightedRowId: [],
          sortBy: "title",
          searchBy: "",
          sortDirection: SortDirection.ASC,
          filterByPriority: [],
          filterByStatus: [],
          topics: [],
          availableFilters: {},
          activeFilters: {},
          allFilters: {},
        };
        this.table = React.createRef();
      }

      componentDidMount() {
        bcfViewerStore.setActiveFileId(this.props.fileId);
        this.fetch(this.getRequestFormBody());
      }

      componentWillUnmount() {
        bcfViewerStore.clear();
      }

      fetch = async (params?: API_BcfTopicFilter) => {
        await bcfViewerStore.fetch(params);
        this.defineFilters(bcfViewerStore.filters);
      };

      toggleDrawer = () => {
        this.setState({ visible: !this.state.visible }, () => this.props.setConfirmOnCancel(this.state.visible));
      };

      defineFilters = (filters: API_BcfExtensions) => {
        const availableFilters: BcfAllFilters = {};
        // const filtersKeyMap: {
        //   [key in keyof API_BcfExtensions]?: keyof Pick<API_BcfTopicFilter, 'filterByPriority' | 'filterByStatus'>
        // } = {
        //   topicPriorities: 'filterByPriority',
        //   topicStatus: 'filterByStatus',
        //   // topicLabels: 'filterByLables',
        //   // topicTypes: 'filterByTypes'
        // }

        Object.keys(filters).forEach((filter: keyof API_BcfExtensions) => {
          availableFilters[filter] = filters[filter].map((value) => ({ text: value, value }));
        });
        this.setState({ availableFilters });
      };

      parseFilters = (filters: BcfActiveFilters) => {
        const activeFilters: API_BcfTopicFilter = {};
        const filtersKeyMap: {
          [key in keyof API_BcfTopic]?: keyof Pick<API_BcfTopicFilter, "filterByPriority" | "filterByStatus">;
        } = {
          priority: "filterByPriority",
          topicStatus: "filterByStatus",
        };
        Object.keys(filters).forEach((filter: keyof BcfActiveFilters) => {
          activeFilters[filtersKeyMap[filter]] = filters[filter].map((value) => value);
        });
        return activeFilters;
      };

      handleSort = (sortBy: keyof API_BcfTopic, sortDirection: SortDirectionType) => {
        const shallReFetch = sortBy !== this.state.sortBy || sortDirection !== this.state.sortDirection;
        this.setState({ sortBy, sortDirection }, () => {
          if (shallReFetch) {
            this.setState({ highlightedRowId: [] });
            this.fetch(this.getRequestFormBody());
          }
        });
      };

      handleFilter = (activeFilters: { [x: string]: string[] }) => {
        const shallReFetch = JSON.stringify(activeFilters) !== JSON.stringify(this.state.activeFilters);
        this.setState({ activeFilters }, () => {
          if (shallReFetch) {
            this.setState({ highlightedRowId: [] });
            this.fetch(this.getRequestFormBody());
          }
        });
      };

      handleSearch = (searchBy: string) => {
        this.setState({ highlightedRowId: [] });
        this.setState({ searchBy }, () => {
          this.fetch(this.getRequestFormBody());
        });
      };

      handleRowClick = async ({ rowData }: { rowData: API_BcfTopic }) => {
        this.setState({ highlightedRowId: [rowData.guid] });
        this.toggleDrawer();
        bcfViewerStore.fetchTopicById(rowData.guid);
      };

      getRequestFormBody(queries?: API_BcfTopicFilter) {
        const params: API_BcfTopicFilter = {};
        const parsedFilters = this.parseFilters(this.state.activeFilters);
        params.sortBy = this.state.sortBy;
        params.sortOrder = this.state.sortDirection;
        params.searchBy = this.state.searchBy;
        params.filterByPriority = parsedFilters.filterByPriority;
        params.filterByStatus = parsedFilters.filterByStatus;
        return { ...params, ...queries };
      }

      get columns(): TableColumnProps<API_BcfTopic>[] {
        const { t } = this.props;
        return [
          {
            dataKey: "title",
            width: 250,
            label: t("Title"),
            flexGrow: 1,
          },
          {
            dataKey: "creationDate",
            width: 250,
            label: t("Date"),
            cellRenderer: ({ cellData }) => (cellData ? cellData : "-"),
          },
          {
            dataKey: "topicStatus",
            width: 150,
            label: t("Status"),
            filters: this.state.availableFilters.topicStatus,
            filterPredicate: (filter, value) => filter === `${value}`,
          },
          {
            dataKey: "priority",
            width: 150,
            label: t("Priority"),
            filters: this.state.availableFilters.topicPriorities,
            filterPredicate: (filter, value) => filter === `${value}`,
          },
        ];
      }
      render() {
        const { t } = this.props;

        return (
          <div className="flex-column" style={{ height: "100%" }}>
            <Input.Search
              allowClear
              onSearch={this.handleSearch}
              placeholder={`${t("Search in title")}...`}
              className="mb-2"
            />
            <ScrollableTable<API_BcfTopic>
              ref={this.table}
              loading={bcfViewerStore.state === "LOADING"}
              rowKey="guid"
              stretchHeight={true}
              data={bcfViewerStore.topics}
              columns={this.columns}
              sortBy={this.state.sortBy}
              sortDirection={this.state.sortDirection}
              onSort={this.handleSort}
              onFilter={this.handleFilter}
              onRowClick={this.handleRowClick}
              highlightedRowId={this.state.highlightedRowId}
            />
            <BCFViewerDrawer
              visible={this.state.visible}
              loading={bcfViewerStore.state === "LOADING"}
              onClose={this.toggleDrawer}
              topic={bcfViewerStore.activeTopic}
            />
          </div>
        );
      }
    }
  )
);
